import { AlertCircle } from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'
import type { InviteeFormSchemaType } from '../utils/inviteeValidators'
import styles from '../styles/inviteeAdd.module.css'

interface PropertyConfirmationsProps {
  form: UseFormReturn<InviteeFormSchemaType>
}

export function PropertyConfirmations({ form }: PropertyConfirmationsProps) {
  const {
    formState: { errors },
    watch,
    setValue,
  } = form

  const crime = watch('crime')
  const approved_by_liberty = watch('approved_by_liberty')
  const allow_to_movein = watch('allow_to_movein')

  return (
    <>
      <h2
        data-testid="invitee-add-section-property-confirmations-title"
        className={styles.sectionTitle}
      >
        Property Confirmations
      </h2>

      {/* Question 1: Criminal Background */}
      <div
        data-testid="invitee-add-property-confirmations-crime-question"
        className={styles.formRow}
      >
        <div className={`${styles.formColWide} ${styles.formColWideOffset}`}>
          <ul className={styles.radioGroup}>
            <li className={styles.radioGroupItem}>
              <label
                data-testid="invitee-add-property-confirmations-crime-label"
                className={styles.radioGroupQuestion}
              >
                Does the applicant's criminal background prohibit them from staying at your property?{' '}
                <span className={styles.requiredStar}>*</span>
              </label>
            </li>
            <li className={styles.radioGroupItem}>
              <div className={styles.radioButtonsContainer}>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name="crime"
                    value="1"
                    checked={crime === '1'}
                    onChange={() => setValue('crime', '1', { shouldValidate: true })}
                    className={styles.radioInput}
                    data-testid="invitee-add-property-confirmations-crime-yes-radio"
                  />
                  <span className={styles.radioLabel}>Yes</span>
                </label>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name="crime"
                    value="0"
                    checked={crime === '0'}
                    onChange={() => setValue('crime', '0', { shouldValidate: true })}
                    className={styles.radioInput}
                    data-testid="invitee-add-property-confirmations-crime-no-radio"
                  />
                  <span className={styles.radioLabel}>No</span>
                </label>
              </div>
            </li>
            <li className={styles.radioGroupItem}>
              <label className={`${styles.radioGroupQuestion} ${styles.radioGroupTerms}`}>
                * You must run the applicant's criminal history before referring them to Liberty Rent
              </label>
            </li>
          </ul>
          {errors.crime && (
            <div
              data-testid="invitee-add-property-confirmations-crime-error"
              className={styles.errorMessage}
            >
              <AlertCircle className="h-4 w-4" />
              Selection is required.
            </div>
          )}
        </div>
      </div>

      {/* Question 2: Unit Availability */}
      <div
        data-testid="invitee-add-property-confirmations-unit-available-question"
        className={styles.formRow}
      >
        <div className={`${styles.formColWide} ${styles.formColWideOffset}`}>
          <ul className={styles.radioGroup}>
            <li className={styles.radioGroupItem}>
              <label
                data-testid="invitee-add-property-confirmations-unit-available-label"
                className={styles.radioGroupQuestion}
              >
                Is there a unit available or coming available for the applicant, if they are approved by Liberty Rent?{' '}
                <span className={styles.requiredStar}>*</span>
              </label>
            </li>
            <li className={styles.radioGroupItem}>
              <div className={styles.radioButtonsContainer}>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name="approved_by_liberty"
                    value="1"
                    checked={approved_by_liberty === '1'}
                    onChange={() => setValue('approved_by_liberty', '1', { shouldValidate: true })}
                    className={styles.radioInput}
                    data-testid="invitee-add-property-confirmations-unit-available-yes-radio"
                  />
                  <span className={styles.radioLabel}>Yes</span>
                </label>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name="approved_by_liberty"
                    value="0"
                    checked={approved_by_liberty === '0'}
                    onChange={() => setValue('approved_by_liberty', '0', { shouldValidate: true })}
                    className={styles.radioInput}
                    data-testid="invitee-add-property-confirmations-unit-available-no-radio"
                  />
                  <span className={styles.radioLabel}>No</span>
                </label>
              </div>
            </li>
          </ul>
          {errors.approved_by_liberty && (
            <div
              data-testid="invitee-add-property-confirmations-unit-available-error"
              className={styles.errorMessage}
            >
              <AlertCircle className="h-4 w-4" />
              Selection is required.
            </div>
          )}
        </div>
      </div>

      {/* Question 3: Move-in Guidelines */}
      <div
        data-testid="invitee-add-property-confirmations-movein-guidelines-question"
        className={styles.formRow}
      >
        <div className={`${styles.formColWide} ${styles.formColWideOffset}`}>
          <ul className={styles.radioGroup}>
            <li className={styles.radioGroupItem}>
              <label
                data-testid="invitee-add-property-confirmations-movein-guidelines-label"
                className={styles.radioGroupQuestion}
              >
                Does your community have any guidelines that would prohibit this applicant from moving in, even if they are approved by Liberty Rent?{' '}
                <span className={styles.requiredStar}>*</span>
              </label>
            </li>
            <li className={styles.radioGroupItem}>
              <div className={styles.radioButtonsContainer}>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name="allow_to_movein"
                    value="1"
                    checked={allow_to_movein === '1'}
                    onChange={() => setValue('allow_to_movein', '1', { shouldValidate: true })}
                    className={styles.radioInput}
                    data-testid="invitee-add-property-confirmations-movein-guidelines-yes-radio"
                  />
                  <span className={styles.radioLabel}>Yes</span>
                </label>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name="allow_to_movein"
                    value="0"
                    checked={allow_to_movein === '0'}
                    onChange={() => setValue('allow_to_movein', '0', { shouldValidate: true })}
                    className={styles.radioInput}
                    data-testid="invitee-add-property-confirmations-movein-guidelines-no-radio"
                  />
                  <span className={styles.radioLabel}>No</span>
                </label>
              </div>
            </li>
          </ul>
          {errors.allow_to_movein && (
            <div
              data-testid="invitee-add-property-confirmations-movein-guidelines-error"
              className={styles.errorMessage}
            >
              <AlertCircle className="h-4 w-4" />
              Selection is required.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
